import { runBulk } from '@/components/ui/run-bulk';
import { apiClient } from '@/lib/api-client';
import { problemCode } from '../api/problem';

export type GrantOutcome = {
  readonly granted: number;
  readonly failed: number;
  /** The first refusal's body and status, to say it in plain words. */
  readonly firstFailure: { readonly body: unknown; readonly status: number } | null;
};

/**
 * Grants each (person, role) pair at the place — the contract has one grant per call — one after
 * another, so a refusal halfway is reported with what did go through rather than as all-or-nothing.
 * A pair the person already holds (ROLE_ASSIGNMENT_ALREADY_GRANTED) is what the act wanted anyway,
 * and counts as granted.
 */
export async function grantPairs(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
  pairs: readonly { readonly userId: string; readonly roleId: string }[],
): Promise<GrantOutcome> {
  let firstFailure: GrantOutcome['firstFailure'] = null;
  const { succeeded, failed } = await runBulk(pairs, async ({ userId, roleId }) => {
    const { error, response } = await apiClient.POST(
      '/v1/administration/{placeKind}/{placeId}/role-assignments',
      { params: { path: { placeKind, placeId } }, body: { userId, roleId } },
    );
    if (response.ok || problemCode(error) === 'ROLE_ASSIGNMENT_ALREADY_GRANTED') {
      return true;
    }
    firstFailure ??= { body: error, status: response.status };
    return false;
  });
  return { granted: succeeded, failed, firstFailure };
}
